import type { paths } from '@luxuryescapes/contract-svc-lux-loyalty'
import requestUtils from 'api/requestUtils'
import { arrayToObject, nonNullable } from 'lib/array/arrayUtils'
import qs from 'qs'
import { getConfiguredTransferOffers } from 'loyalty/lib/transferOfferUtils'

type Response = paths['/api/lux-loyalty/program-configuration']['get']['responses']['200']['content']['application/json']
type ServerBenefit = Response['result']['benefits'][0]
type ServerTier = Response['result']['tiers'][0]

type LoyaltyServerBenefit = ServerBenefit & { code: App.LoyaltyBenefitType }

type AirportTransferBenefit = ServerBenefit & {
  code: 'airport_transfer'
  homeToAirportEnabled?: boolean
  transferOffers?: App.LoyaltyTransferOffers
}

function isLoyaltyServerBenefit(
  benefit: ServerBenefit,
): benefit is LoyaltyServerBenefit {
  return benefit.code !== 'travelshoot_credit'
}

function isAirportTransferBenefit(benefit: ServerBenefit): benefit is AirportTransferBenefit {
  return benefit.code === 'airport_transfer'
}

function mapTierBenefits(
  benefits: Array<LoyaltyServerBenefit>,
  tier: App.LoyaltyTier,
): Array<App.LoyaltyRegionTierBenefit> {
  return nonNullable(benefits.map(benefit => {
    const tierBenefit = benefit.tiers[tier]
    if (!tierBenefit?.available) return
    return {
      code: benefit.code,
      limit: tierBenefit.limit === 'unlimited' ? undefined : tierBenefit.limit,
      unlimited: tierBenefit.limit === 'unlimited',
      value: tierBenefit.value,
    } }))
}

function mapTierThresholds(
  tiers: Array<ServerTier>,
): Record<App.LoyaltyTier, App.LoyaltyTierThreshold> {
  const thresholds = {} as Record<App.LoyaltyTier, App.LoyaltyTierThreshold>

  tiers.forEach(tier => {
    thresholds[tier.id] = {
      statusCredits: tier.statusCredits,
      nights: tier.nights,
    }
  })

  return thresholds
}

async function requestLoyaltyConfig(regionCode: string): Promise<App.LoyaltyConfig> {
  const query = qs.stringify({
    region: regionCode,
  })

  return requestUtils.get<Response>(
    `/api/lux-loyalty/program-configuration?${query}`,
    { excludeBrand: true },
  ).then((response): App.LoyaltyConfig => {
    const serverBenefits = response.result.benefits
    const loyaltyBenefits = serverBenefits.filter(isLoyaltyServerBenefit)
    const loungePassBenefit = serverBenefits.find(benefit => benefit.code === 'lounge_pass')
    const transferBenefit = serverBenefits.find(isAirportTransferBenefit)
    const esimBenefit = serverBenefits.find(benefit => benefit.code === 'esim_package')
    const transferOffers = transferBenefit?.transferOffers
    const transferOfferEntries = getConfiguredTransferOffers(transferOffers)
    const benefitsLookup = arrayToObject(
      serverBenefits,
      (benefit) => benefit.code,
      (benefit) => benefit,
    )

    const mappedTransferOffers = transferOfferEntries.length > 0 ? transferOffers : undefined
    return {
      region: regionCode,
      loungePassExperienceIds: loungePassBenefit?.offerIds ?? [],
      transferOffers: mappedTransferOffers,
      transferAirports: transferBenefit?.eligibleAirports ?? [],
      homeToAirportEnabled: !!transferBenefit?.transferOffers?.loyaltyHomeToAirport,
      benefits: loyaltyBenefits.map(benefit => ({
        code: benefit.code,
        paymentType: benefit.lePaymentType,
      })),
      regionEnabledBenefits: {
        airport_transfer: !!benefitsLookup.airport_transfer,
        discounted_insurance: !!benefitsLookup.discounted_insurance,
        early_access: !!benefitsLookup.early_access,
        esim_package: !!benefitsLookup.esim_package,
        hidden_access: !!benefitsLookup.hidden_access,
        lounge_pass: !!benefitsLookup.lounge_pass,
        phone_support: !!benefitsLookup.phone_support,
        tour_exp_credit: !!benefitsLookup.tour_exp_credit,
        hotel_upgrade: !!benefitsLookup.hotel_upgrade,
        family_and_friends_access: !!benefitsLookup.family_and_friends_access,
      },
      regionTierBenefits: {
        bronze: mapTierBenefits(loyaltyBenefits, 'bronze'),
        silver: mapTierBenefits(loyaltyBenefits, 'silver'),
        gold: mapTierBenefits(loyaltyBenefits, 'gold'),
        platinum: mapTierBenefits(loyaltyBenefits, 'platinum'),
        infinite: mapTierBenefits(loyaltyBenefits, 'infinite'),
      },
      tierThresholds: mapTierThresholds(response.result.tiers),
      esimExcludedCountryCodes: esimBenefit?.excludedCountryCodes ?? [],
    }
  })
}

export default requestLoyaltyConfig
